import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type PolarMode = "rect-to-polar" | "polar-to-rect";

export interface PolarCoordinatesParams {
  mode: PolarMode;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const PolarCoordinates: Filter<PolarCoordinatesParams> = {
  id: "polar-coordinates",
  displayName: "Polar Coordinates",
  defaultParams: { mode: "rect-to-polar" },

  apply(input: PixelBuffer, params: PolarCoordinatesParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const cx = w / 2, cy = h / 2;
    const maxDist = Math.sqrt(cx * cx + cy * cy);

    if (params.mode === "rect-to-polar") {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const angle = (x / w) * Math.PI * 2;
          const radius = (y / h) * maxDist;
          const sx = Math.round(cx + Math.cos(angle) * radius);
          const sy = Math.round(cy + Math.sin(angle) * radius);

          const o = (y * w + x) * 4;
          if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
            const si = (sy * w + sx) * 4;
            dst[o] = src[si]!; dst[o + 1] = src[si + 1]!; dst[o + 2] = src[si + 2]!; dst[o + 3] = src[si + 3]!;
          } else {
            dst[o] = 0; dst[o + 1] = 0; dst[o + 2] = 0; dst[o + 3] = 0;
          }
        }
      }
    } else {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const dx = x - cx, dy = y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          const nx = ((angle / (Math.PI * 2)) + 1) % 1;
          const ny = dist / maxDist;
          const sx = Math.round(nx * w);
          const sy = Math.round(ny * h);

          const o = (y * w + x) * 4;
          if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
            const si = (sy * w + sx) * 4;
            dst[o] = src[si]!; dst[o + 1] = src[si + 1]!; dst[o + 2] = src[si + 2]!; dst[o + 3] = src[si + 3]!;
          } else {
            dst[o] = 0; dst[o + 1] = 0; dst[o + 2] = 0; dst[o + 3] = 0;
          }
        }
      }
    }
    return out;
  },
};
